import React, { useState, useEffect } from 'react';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Ruler, Trash2, Calendar, TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface BodyMeasurementsTrackerProps {
  selectedDate: string;
  refreshTrigger: number;
  onMeasurementChange: () => void;
  clientId?: number;
}

interface GrowthStat {
  start: number;
  end: number;
  diff: string;
  isPositive: boolean;
  isNegative: boolean;
}

export default function BodyMeasurementsTracker({
  selectedDate,
  refreshTrigger,
  onMeasurementChange,
  clientId,
}: BodyMeasurementsTrackerProps) {
  const isClientView = !!clientId;
  const [chest, setChest] = useState('');
  const [leftBicep, setLeftBicep] = useState('');
  const [rightBicep, setRightBicep] = useState('');
  const [waist, setWaist] = useState('');
  const [leftThigh, setLeftThigh] = useState('');
  const [rightThigh, setRightThigh] = useState('');
  const [leftCalf, setLeftCalf] = useState('');
  const [rightCalf, setRightCalf] = useState('');
  const [notes, setNotes] = useState('');

  const utils = trpc.useUtils();

  const { data: ownLogs = [] } = trpc.measurements.list.useQuery(undefined, { enabled: !isClientView });
  const { data: clientLogs = [] } = trpc.trainerPortal.getClientMeasurementLogs.useQuery(
    { clientId: clientId! },
    { enabled: isClientView && !!clientId }
  );
  const rawLogs = isClientView ? clientLogs : ownLogs;

  const logs = rawLogs.map((l) => ({
    id: l.id,
    date: l.date,
    chest: l.chest ?? undefined,
    leftBicep: l.leftBicep ?? undefined,
    rightBicep: l.rightBicep ?? undefined,
    waist: l.waist ?? undefined,
    leftThigh: l.leftThigh ?? undefined,
    rightThigh: l.rightThigh ?? undefined,
    leftCalf: l.leftCalf ?? undefined,
    rightCalf: l.rightCalf ?? undefined,
    notes: l.notes ?? undefined,
  }));

  useEffect(() => {
    const todayLog = logs.find((l) => l.date === selectedDate);
    if (todayLog) {
      setChest(todayLog.chest?.toString() || '');
      setLeftBicep(todayLog.leftBicep?.toString() || '');
      setRightBicep(todayLog.rightBicep?.toString() || '');
      setWaist(todayLog.waist?.toString() || '');
      setLeftThigh(todayLog.leftThigh?.toString() || '');
      setRightThigh(todayLog.rightThigh?.toString() || '');
      setLeftCalf(todayLog.leftCalf?.toString() || '');
      setRightCalf(todayLog.rightCalf?.toString() || '');
      setNotes(todayLog.notes || '');
    } else {
      setChest(''); setLeftBicep(''); setRightBicep(''); setWaist('');
      setLeftThigh(''); setRightThigh(''); setLeftCalf(''); setRightCalf('');
      setNotes('');
    }
  }, [selectedDate, rawLogs.length]);

  const saveMeasurementMutation = trpc.measurements.save.useMutation({
    onSuccess: () => { utils.measurements.list.invalidate(); onMeasurementChange(); },
  });

  const deleteMeasurementMutation = trpc.measurements.delete.useMutation({
    onSuccess: () => { utils.measurements.list.invalidate(); onMeasurementChange(); },
  });

  const handleSaveMeasurements = async (e: React.FormEvent) => {
    e.preventDefault();
    const parseVal = (val: string) => { const p = parseFloat(val); return isNaN(p) || p <= 0 ? undefined : p; };
    const payload = {
      date: selectedDate,
      chest: parseVal(chest), leftBicep: parseVal(leftBicep), rightBicep: parseVal(rightBicep),
      waist: parseVal(waist), leftThigh: parseVal(leftThigh), rightThigh: parseVal(rightThigh),
      leftCalf: parseVal(leftCalf), rightCalf: parseVal(rightCalf),
      notes: notes.trim() || undefined,
    };
    const hasValues = Object.entries(payload).some(([k, v]) => k !== 'date' && k !== 'notes' && v !== undefined);
    if (!hasValues) { toast.error('Please enter at least one measurement value'); return; }
    await saveMeasurementMutation.mutateAsync(payload);
    toast.success(`Measurements recorded for ${selectedDate}`);
  };

  const handleDeleteLog = async (id: string, date: string) => {
    if (confirm(`Delete body measurements log for ${date}?`)) {
      await deleteMeasurementMutation.mutateAsync({ logId: id });
      toast.success('Measurement log deleted');
    }
  };

  const sortedLogs = [...logs].sort((a, b) => a.date.localeCompare(b.date));
  const latestLog = sortedLogs.length > 0 ? sortedLogs[sortedLogs.length - 1] : null;

  const getGrowthStats = () => {
    if (sortedLogs.length < 2) return null;
    const earliest = sortedLogs[0];
    const latest = sortedLogs[sortedLogs.length - 1];
    const calcDiff = (key: keyof Omit<typeof earliest, 'id' | 'date' | 'notes'>): GrowthStat | null => {
      const start = earliest[key] as number | undefined;
      const end = latest[key] as number | undefined;
      if (start && end) {
        const diff = end - start;
        return { start, end, diff: diff.toFixed(2), isPositive: diff > 0, isNegative: diff < 0 };
      }
      return null;
    };
    return {
      chest: calcDiff('chest'), leftBicep: calcDiff('leftBicep'), rightBicep: calcDiff('rightBicep'),
      waist: calcDiff('waist'), leftThigh: calcDiff('leftThigh'), rightThigh: calcDiff('rightThigh'),
      leftCalf: calcDiff('leftCalf'), rightCalf: calcDiff('rightCalf'),
    };
  };
  const growth = getGrowthStats();

  const GrowthBadge = ({ stat, invert = false }: { stat: GrowthStat | null; invert?: boolean }) => {
    if (!stat) return <span className="text-[10px] font-mono text-muted-foreground/50">—</span>;
    const diff = parseFloat(stat.diff);
    if (diff === 0) return <span className="text-[10px] font-mono text-muted-foreground flex items-center gap-0.5"><Minus className="h-2.5 w-2.5" />0.00"</span>;
    // For waist: decrease is good (invert=true)
    const isGood = invert ? diff < 0 : diff > 0;
    const Icon = diff > 0 ? TrendingUp : TrendingDown;
    const colorClass = isGood ? 'text-success' : 'text-destructive';
    return (
      <span className={`text-[10px] font-mono font-bold flex items-center gap-0.5 ${colorClass}`}>
        <Icon className="h-2.5 w-2.5" />
        {diff > 0 ? `+${stat.diff}` : stat.diff}"
      </span>
    );
  };

  // Summary grid data
  const summaryItems = [
    { label: 'Chest', current: latestLog?.chest, stat: growth?.chest, invert: false },
    { label: 'L Bicep', current: latestLog?.leftBicep, stat: growth?.leftBicep, invert: false },
    { label: 'R Bicep', current: latestLog?.rightBicep, stat: growth?.rightBicep, invert: false },
    { label: 'Waist', current: latestLog?.waist, stat: growth?.waist, invert: true },
    { label: 'L Thigh', current: latestLog?.leftThigh, stat: growth?.leftThigh, invert: false },
    { label: 'R Thigh', current: latestLog?.rightThigh, stat: growth?.rightThigh, invert: false },
    { label: 'L Calf', current: latestLog?.leftCalf, stat: growth?.leftCalf, invert: false },
    { label: 'R Calf', current: latestLog?.rightCalf, stat: growth?.rightCalf, invert: false },
  ];

  return (
    <div className="forge-panel p-4 space-y-5">
      <div className="forge-header">
        <span>Tape Measurements</span>
        <span className="text-primary font-mono flex items-center gap-1">
          <Ruler className="h-3.5 w-3.5" /> CALIPERS
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* ── Left: Input Form ── */}
        {!isClientView && (
          <div className="lg:col-span-5">
            <form onSubmit={handleSaveMeasurements} className="space-y-4 bg-secondary/10 border border-border/40 rounded-sm p-4">
              <div className="text-[10px] font-mono text-muted-foreground flex items-center gap-1">
                <Calendar className="h-3 w-3 text-primary" /> {selectedDate}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-[10px] font-mono text-muted-foreground uppercase mb-1">Chest</label>
                  <div className="relative">
                    <Input type="number" step="0.125" placeholder="42.5" value={chest} onChange={(e) => setChest(e.target.value)} className="bg-secondary border-border font-mono text-sm pr-8" />
                    <span className="absolute right-2 top-2.5 text-[10px] font-mono text-muted-foreground">IN</span>
                  </div>
                </div>
                {[
                  { label: 'L Bicep', val: leftBicep, set: setLeftBicep },
                  { label: 'R Bicep', val: rightBicep, set: setRightBicep },
                  { label: 'Waist', val: waist, set: setWaist, full: true },
                  { label: 'L Thigh', val: leftThigh, set: setLeftThigh },
                  { label: 'R Thigh', val: rightThigh, set: setRightThigh },
                  { label: 'L Calf', val: leftCalf, set: setLeftCalf },
                  { label: 'R Calf', val: rightCalf, set: setRightCalf },
                ].map(({ label, val, set, full }) => (
                  <div key={label} className={full ? 'col-span-2' : ''}>
                    <label className="block text-[10px] font-mono text-muted-foreground uppercase mb-1">{label}</label>
                    <div className="relative">
                      <Input type="number" step="0.125" placeholder="—" value={val} onChange={(e) => set(e.target.value)} className="bg-secondary border-border font-mono text-sm pr-8" />
                      <span className="absolute right-2 top-2.5 text-[10px] font-mono text-muted-foreground">IN</span>
                    </div>
                  </div>
                ))}
              </div>

              <div>
                <label className="block text-[10px] font-mono text-muted-foreground uppercase mb-1">Notes</label>
                <Input placeholder="Morning, cold measurement…" value={notes} onChange={(e) => setNotes(e.target.value)} className="bg-secondary border-border text-xs" />
              </div>

              <Button type="submit" disabled={saveMeasurementMutation.isPending} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-xs tracking-widest">
                RECORD MEASUREMENTS
              </Button>
            </form>
          </div>
        )}

        {/* ── Right: Growth Summary + Log Archive ── */}
        <div className={`${isClientView ? 'lg:col-span-12' : 'lg:col-span-7'} space-y-4`}>
          {/* All-Time Growth Summary */}
          <div className="bg-secondary/20 border border-border/40 rounded-sm p-4 space-y-3">
            <div className="text-[10px] font-mono text-primary uppercase tracking-widest flex items-center gap-1.5">
              <TrendingUp className="h-3.5 w-3.5" /> All-Time Growth Summary
            </div>

            {sortedLogs.length < 2 ? (
              <div className="flex flex-col items-center justify-center py-8 space-y-2 border border-dashed border-border/60 rounded-sm">
                <Ruler className="h-6 w-6 text-muted-foreground/25" />
                <div className="text-xs font-mono text-muted-foreground text-center">
                  Log on <strong className="text-foreground">2+ days</strong> to see your growth deltas.
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-2">
                {summaryItems.map(({ label, current, stat, invert }) => (
                  <div key={label} className="bg-background border border-border/40 rounded-sm p-2.5 space-y-1">
                    <div className="text-[9px] font-mono text-muted-foreground uppercase tracking-wider">{label}</div>
                    <div className="text-sm font-mono font-bold text-foreground leading-none">
                      {current != null ? `${current}"` : <span className="text-muted-foreground/40">—</span>}
                    </div>
                    <GrowthBadge stat={stat ?? null} invert={invert} />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Measurement Log Archive */}
          <div className="space-y-2">
            <div className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider border-b border-border/30 pb-1 flex justify-between items-center">
              <span>Measurement Archive</span>
              <span className="text-[9px] font-normal">{logs.length} logged</span>
            </div>

            {logs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 space-y-1 border border-dashed border-border/60 rounded-sm">
                <Ruler className="h-5 w-5 text-muted-foreground/25" />
                <div className="text-xs font-mono text-muted-foreground">No measurement logs yet.</div>
              </div>
            ) : (
              <div className="max-h-[240px] overflow-y-auto space-y-1.5 pr-1">
                {[...logs]
                  .sort((a, b) => b.date.localeCompare(a.date))
                  .map((l) => (
                    <div key={l.id} className="p-3 bg-secondary/20 border border-border/40 rounded-sm space-y-2 group">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs font-bold text-primary">{l.date}</span>
                          {l.notes && <span className="text-[10px] text-muted-foreground italic truncate max-w-[200px]">({l.notes})</span>}
                        </div>
                        {!isClientView && (
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleDeleteLog(l.id, l.date)}
                            className="h-6 w-6 text-muted-foreground/40 hover:text-destructive opacity-0 group-hover:opacity-100 md:opacity-100 transition-opacity"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-4 gap-x-3 gap-y-1 text-[11px] font-mono">
                        {l.chest && <div className="text-muted-foreground">Chest <strong className="text-foreground">{l.chest}"</strong></div>}
                        {(l.leftBicep || l.rightBicep) && (
                          <div className="col-span-2 text-muted-foreground">
                            Arms <strong className="text-foreground">L {l.leftBicep || '—'}" / R {l.rightBicep || '—'}"</strong>
                          </div>
                        )}
                        {l.waist && <div className="text-muted-foreground">Waist <strong className="text-foreground">{l.waist}"</strong></div>}
                        {(l.leftThigh || l.rightThigh) && (
                          <div className="col-span-2 text-muted-foreground">
                            Thighs <strong className="text-foreground">L {l.leftThigh || '—'}" / R {l.rightThigh || '—'}"</strong>
                          </div>
                        )}
                        {(l.leftCalf || l.rightCalf) && (
                          <div className="col-span-2 text-muted-foreground">
                            Calves <strong className="text-foreground">L {l.leftCalf || '—'}" / R {l.rightCalf || '—'}"</strong>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
